import React, { useEffect, useState } from "react";
import {
    Box, Typography, Card, CardContent, TextField, Button,
    Avatar, Snackbar, Alert, IconButton, Tooltip, Skeleton,
} from "@mui/material";
import EditRoundedIcon          from "@mui/icons-material/EditRounded";
import SaveRoundedIcon          from "@mui/icons-material/SaveRounded";
import CloseRoundedIcon         from "@mui/icons-material/CloseRounded";
import PersonRoundedIcon        from "@mui/icons-material/PersonRounded";
import PhoneRoundedIcon         from "@mui/icons-material/PhoneRounded";
import LocationOnRoundedIcon    from "@mui/icons-material/LocationOnRounded";
import CameraAltRoundedIcon     from "@mui/icons-material/CameraAltRounded";
import ArrowBackRoundedIcon     from "@mui/icons-material/ArrowBackRounded";
import { useNavigate }          from "react-router-dom";
import { getData, postData, API_ENDPOINTS } from "../../services/apiService.js";

// ── Brand palette ─────────────────────────────────────────────────────────
const T = {
    teal:    "#007C84", tealDk: "#016268", tealLt: "#E0F4F5",
    emerald: "#059669", emeraldLt: "#D1FAE5",
    bg:      "#F7FAFC", border: "#E2E8F0",
    text:    "#0F172A", muted:  "#64748B",
};

const FIELDS = [
    { key:"first_name", label:"First Name",  icon:<PersonRoundedIcon />,       type:"text"  },
    { key:"last_name",  label:"Last Name",   icon:<PersonRoundedIcon />,       type:"text"  },
    { key:"city",       label:"City",        icon:<LocationOnRoundedIcon />,   type:"text"  },
    { key:"state",      label:"State",       icon:<LocationOnRoundedIcon />,   type:"text"  },
    { key:"mobile_num", label:"Mobile",      icon:<PhoneRoundedIcon />,        type:"tel"   },
];

const AVATAR_COLORS = [
    ["#E0F4F5","#007C84"],["#FFF0E0","#FF7900"],["#EEF2FF","#4F46E5"],
    ["#D1FAE5","#059669"],["#FEF3C7","#D97706"],["#FFE4E6","#E11D48"],
];
const avatarBg = name => AVATAR_COLORS[name?.charCodeAt(0) % AVATAR_COLORS.length] || AVATAR_COLORS[0];
const initials = name => {
    const p = (name||"").trim().split(" ");
    return p.length >= 2 ? (p[0][0]+p[1][0]).toUpperCase() : (p[0]?.[0]||"?").toUpperCase();
};

// ── Info row (view mode) ──────────────────────────────────────────────────
const InfoRow = ({ label, value, icon }) => (
    <Box sx={{ display:"flex", alignItems:"center", py:1.5, borderBottom:`1px solid ${T.border}`, "&:last-child":{ borderBottom:"none" }, gap:2 }}>
        <Box sx={{ width:34,height:34,borderRadius:"9px",bgcolor:T.tealLt,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
            {React.cloneElement(icon, { sx:{ fontSize:16,color:T.teal } })}
        </Box>
        <Box sx={{ flex:1 }}>
            <Typography sx={{ fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.3px" }}>{label}</Typography>
            <Typography sx={{ fontSize:14,fontWeight:600,color:T.text,mt:0.2 }}>{value||"—"}</Typography>
        </Box>
    </Box>
);

// ── Main component ────────────────────────────────────────────────────────
export default function Profile() {
    const navigate = useNavigate();
    const [admin,       setAdmin]        = useState(null);
    const [formData,    setFormData]     = useState({});
    const [editMode,    setEditMode]     = useState(false);
    const [saving,      setSaving]       = useState(false);
    const [loading,     setLoading]      = useState(true);
    const [previewImg,  setPreviewImg]   = useState("");
    const [snack,       setSnack]        = useState({ open:false, msg:"", sev:"success" });

    const toast = (msg, sev="success") => setSnack({ open:true, msg, sev });

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const raw = localStorage.getItem("userData");
                if (!raw) return;
                const adminId = JSON.parse(raw).admin_id;
                if (!adminId) return;
                const res = await getData(API_ENDPOINTS.getAdminById, { id:adminId });
                const d = res.result;
                setAdmin(d); setFormData(d); setPreviewImg(d.pic);
            } catch(_) { toast("Failed to load profile","error"); }
            finally { setLoading(false); }
        })();
    }, []);

    const handleImageChange = e => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => setPreviewImg(reader.result);
        reader.readAsDataURL(file);
        setFormData(p => ({ ...p, pic:file }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const raw = localStorage.getItem("userData");
            const adminId = JSON.parse(raw||"{}").admin_id;
            const fd = new FormData();
            fd.append("id", adminId);
            ["first_name","last_name","mobile_num","city","state"].forEach(k => fd.append(k, formData[k]||""));
            if (formData.pic instanceof File) fd.append("pic", formData.pic);

            const res = await postData(API_ENDPOINTS.updateAdmin, fd);
            if (res.status === "SUCCESS") {
                const updated = await getData(API_ENDPOINTS.getAdminById, { id:adminId });
                const d = updated.result;
                setAdmin(d); setFormData(d); setPreviewImg(d.pic);
                setEditMode(false);
                toast("Profile updated successfully!");
            } else throw new Error(res.message||"Update failed");
        } catch(e) { toast(e.message||"Failed to update","error"); }
        finally { setSaving(false); }
    };

    const handleCancel = () => {
        setFormData(admin); setPreviewImg(admin?.pic||""); setEditMode(false);
    };

    const fullName = admin ? `${admin.first_name||""} ${admin.last_name||""}`.trim() : "";
    const [bg, fg] = avatarBg(fullName);

    return (
        <Box sx={{ bgcolor:T.bg, minHeight:"100vh", pb:4 }}>

            {/* ── Hero header ── */}
            <Box sx={{ background:`linear-gradient(135deg,${T.teal} 0%,${T.tealDk} 100%)`, borderRadius:"20px", p:3, mb:3,
                display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:2,
                boxShadow:"0 4px 20px rgba(0,124,132,0.2)" }}>
                <Box sx={{ display:"flex", alignItems:"center", gap:2 }}>
                    <IconButton onClick={()=>navigate("/app/settings")}
                        sx={{ bgcolor:"rgba(255,255,255,0.15)",border:"1px solid rgba(255,255,255,0.25)",borderRadius:"10px",color:"#fff","&:hover":{ bgcolor:"rgba(255,255,255,0.25)" } }}>
                        <ArrowBackRoundedIcon sx={{ fontSize:18 }} />
                    </IconButton>
                    <Box>
                        <Typography sx={{ fontSize:22,fontWeight:800,color:"#fff",fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                            {loading ? "My Profile" : fullName||"Admin Profile"}
                        </Typography>
                        <Typography sx={{ fontSize:13,color:"rgba(255,255,255,0.75)",mt:0.3 }}>
                            Manage your personal information and account settings
                        </Typography>
                    </Box>
                </Box>
                {!editMode && (
                    <Tooltip title="Edit profile">
                        <Button variant="outlined" size="small" startIcon={<EditRoundedIcon sx={{ fontSize:14 }} />}
                            onClick={()=>setEditMode(true)} disabled={loading}
                            sx={{ borderRadius:"10px",textTransform:"none",fontWeight:600,color:"#fff",borderColor:"rgba(255,255,255,0.4)",bgcolor:"rgba(255,255,255,0.1)","&:hover":{ bgcolor:"rgba(255,255,255,0.2)" } }}>
                            Edit Profile
                        </Button>
                    </Tooltip>
                )}
            </Box>

            {/* ── Profile card ── */}
            <Box sx={{ display:"grid", gridTemplateColumns:{ xs:"1fr", md:"280px 1fr" }, gap:3 }}>

                {/* Left — avatar */}
                <Card sx={{ borderRadius:"16px",border:`1px solid ${T.border}`,boxShadow:"0 1px 4px rgba(15,23,42,0.06)",overflow:"hidden" }}>
                    <Box sx={{ height:3,background:`linear-gradient(90deg,${T.teal},${T.tealLt})` }} />
                    <CardContent sx={{ p:2.5,"&:last-child":{ pb:2.5 },display:"flex",flexDirection:"column",alignItems:"center",gap:2 }}>
                        <Box sx={{ position:"relative" }}>
                            {loading ? (
                                <Skeleton variant="circular" width={120} height={120} />
                            ) : (
                                <Avatar
                                    src={previewImg||""}
                                    sx={{ width:120,height:120,bgcolor:bg,color:fg,fontSize:36,fontWeight:800,border:`3px solid ${T.border}` }}
                                >
                                    {!previewImg && initials(fullName)}
                                </Avatar>
                            )}
                            {editMode && (
                                <Box component="label" sx={{
                                    position:"absolute",bottom:4,right:4,width:32,height:32,borderRadius:"50%",
                                    bgcolor:T.teal,display:"flex",alignItems:"center",justifyContent:"center",
                                    cursor:"pointer",border:"2px solid #fff",boxShadow:"0 2px 8px rgba(0,124,132,0.35)",
                                    "&:hover":{ bgcolor:T.tealDk },
                                }}>
                                    <CameraAltRoundedIcon sx={{ fontSize:16,color:"#fff" }} />
                                    <input type="file" hidden accept="image/*" onChange={handleImageChange} />
                                </Box>
                            )}
                        </Box>

                        {loading ? (
                            <>
                                <Skeleton width={120} height={22} />
                                <Skeleton width={80} height={18} />
                            </>
                        ) : (
                            <>
                                <Typography sx={{ fontSize:16,fontWeight:800,color:T.text,fontFamily:"'Plus Jakarta Sans',sans-serif",textAlign:"center" }}>
                                    {fullName||"Admin"}
                                </Typography>
                                <Box sx={{ px:1.5,py:0.4,bgcolor:T.tealLt,borderRadius:"8px",border:`1px solid ${T.teal}22` }}>
                                    <Typography sx={{ fontSize:11,fontWeight:700,color:T.teal,textTransform:"uppercase",letterSpacing:"0.5px" }}>
                                        {admin?.role||"Administrator"}
                                    </Typography>
                                </Box>
                                <Typography sx={{ fontSize:12,color:T.muted,textAlign:"center" }}>
                                    {admin?.email||""}
                                </Typography>
                            </>
                        )}
                    </CardContent>
                </Card>

                {/* Right — details */}
                <Card sx={{ borderRadius:"16px",border:`1px solid ${T.border}`,boxShadow:"0 1px 4px rgba(15,23,42,0.06)",overflow:"hidden" }}>
                    <Box sx={{ height:3,background:`linear-gradient(90deg,${T.teal},${T.tealLt})` }} />
                    <CardContent sx={{ p:2.5,"&:last-child":{ pb:2.5 } }}>
                        <Box sx={{ display:"flex",justifyContent:"space-between",alignItems:"center",mb:2 }}>
                            <Typography sx={{ fontSize:14,fontWeight:700,color:T.text,fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                                {editMode ? "Edit Information" : "Personal Information"}
                            </Typography>
                            {editMode && (
                                <Typography sx={{ fontSize:11,color:T.muted }}>Fields marked are editable</Typography>
                            )}
                        </Box>

                        {loading ? (
                            Array.from({length:5}).map((_,i) => (
                                <Box key={i} sx={{ display:"flex",alignItems:"center",gap:2,py:1.5,borderBottom:`1px solid ${T.border}` }}>
                                    <Skeleton variant="rounded" width={34} height={34} />
                                    <Box><Skeleton width={80} height={12} sx={{ mb:0.5 }} /><Skeleton width={120} height={16} /></Box>
                                </Box>
                            ))
                        ) : editMode ? (
                            // ── Edit form ──
                            <Box sx={{ display:"flex",flexDirection:"column",gap:2 }}>
                                <Box sx={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:2 }}>
                                    {FIELDS.filter(f=>f.key!=="mobile_num").map(f => (
                                        <TextField key={f.key} label={f.label} type={f.type} size="small"
                                            value={formData[f.key]||""}
                                            onChange={e=>setFormData(p=>({...p,[f.key]:e.target.value}))}
                                            InputProps={{ sx:{ borderRadius:"12px" } }}
                                            sx={{ "& .MuiFormLabel-root":{ fontSize:13 } }}
                                        />
                                    ))}
                                </Box>
                                <TextField label="Mobile" type="tel" size="small"
                                    value={formData.mobile_num||""}
                                    onChange={e=>setFormData(p=>({...p,mobile_num:e.target.value}))}
                                    InputProps={{ sx:{ borderRadius:"12px" } }}
                                    sx={{ "& .MuiFormLabel-root":{ fontSize:13 }, maxWidth:280 }}
                                />
                                <Box sx={{ display:"flex",gap:1.5,mt:1 }}>
                                    <Button variant="contained" disableElevation onClick={handleSave} disabled={saving}
                                        startIcon={saving?null:<SaveRoundedIcon sx={{ fontSize:15 }} />}
                                        sx={{ borderRadius:"10px",textTransform:"none",fontWeight:700,bgcolor:T.teal,"&:hover":{ bgcolor:T.tealDk },px:3 }}>
                                        {saving?"Saving…":"Save Changes"}
                                    </Button>
                                    <Button variant="outlined" onClick={handleCancel} disabled={saving}
                                        startIcon={<CloseRoundedIcon sx={{ fontSize:15 }} />}
                                        sx={{ borderRadius:"10px",textTransform:"none",fontWeight:600,borderColor:T.border,color:T.muted }}>
                                        Cancel
                                    </Button>
                                </Box>
                            </Box>
                        ) : (
                            // ── View mode ──
                            FIELDS.map(f => (
                                <InfoRow key={f.key} label={f.label} value={formData[f.key]} icon={f.icon} />
                            ))
                        )}
                    </CardContent>
                </Card>
            </Box>

            <Snackbar open={snack.open} autoHideDuration={4000} onClose={()=>setSnack(s=>({...s,open:false}))} anchorOrigin={{ vertical:"top",horizontal:"center" }}>
                <Alert severity={snack.sev} variant="filled" onClose={()=>setSnack(s=>({...s,open:false}))} sx={{ borderRadius:"12px" }}>{snack.msg}</Alert>
            </Snackbar>
        </Box>
    );
}
